import type { ApiClient } from '../../lib/api-client.js';
import type {
  PlanningWorkspace,
  PlanningWorkspaceMetadata,
  ScenarioPlanningObjectItem,
  WfRawUser,
  WfRawWorkspaceMetadataResponse,
  WfRawWorkspacesResponse,
} from './erd-bridge.js';
import { toWorkspaceMetadata } from './erd-mapper.js';

/**
 * Gateway over the `/v1/planning/*` customer-API surface, scoped to one
 * Workfront connection (primary by default; secondary:N for multi-tenant
 * licenses). Mirrors `wf-audit/src/slices/planning/data/planning.gateway.ts`
 * minus the demo-mode branch — demo mode for the ER Diagram is a TODO
 * (the iframe needs a Workfront Maestro response shape, which the demo
 * fixtures don't currently cover).
 *
 * Each method maps to one engine round-trip:
 *   - getWorkspaces / getUsers / getWorkspaceMetadata go through the
 *     Maestro session-proxy (`POST /v1/planning/proxy`)
 *   - getFusionUsage hits the own-DB query (`GET /v1/planning/fusion-usage`)
 */
export class ErdGateway {
  constructor(
    private readonly api: ApiClient,
    private readonly workspaceRef: string = 'primary',
  ) {}

  async getWorkspaces(): Promise<PlanningWorkspace[]> {
    const raw = await this.api.post<WfRawWorkspacesResponse>('/v1/planning/proxy', {
      operation: 'getWorkspaces',
      workspaceRef: this.workspaceRef,
    });
    return (raw.workspaces ?? []).map(({ id, name, icon, createdBy }) => ({
      id,
      name,
      icon,
      createdBy: createdBy ?? '',
    }));
  }

  async getUsersByIds(ids: string[]): Promise<Record<string, string>> {
    if (ids.length === 0) return {};
    const raw = await this.api.post<WfRawUser[] | { users?: WfRawUser[] }>('/v1/planning/proxy', {
      operation: 'getUsers',
      workspaceRef: this.workspaceRef,
      userIds: ids,
    });
    // Maestro returns the array directly in some versions, `{users:[...]}` in
    // others — handle both.
    const users: WfRawUser[] = Array.isArray(raw) ? raw : (raw.users ?? []);
    return Object.fromEntries(users.map((u) => [u.id, u.name]));
  }

  async getWorkspaceMetadata(workspaceId: string): Promise<PlanningWorkspaceMetadata> {
    const raw = await this.api.post<WfRawWorkspaceMetadataResponse>('/v1/planning/proxy', {
      operation: 'getWorkspaceMetadata',
      workspaceRef: this.workspaceRef,
      workspaceId,
    });
    return toWorkspaceMetadata(raw);
  }

  async getFusionUsage(workspaceId: string): Promise<ScenarioPlanningObjectItem[]> {
    const res = await this.api.get<{ data?: ScenarioPlanningObjectItem[] }>(
      `/v1/planning/fusion-usage?workspaceId=${encodeURIComponent(workspaceId)}`,
    );
    return res.data ?? [];
  }
}
